import React, { useState } from "react";
import {
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Rectangle,
  Customized,
  Scatter,
  ReferenceArea,
  ScatterChart,
} from "recharts";

import {
  ChartRangeContent,
  getChartHeight,
  getCustomAxisLabel,
  getCustomTooltip,
  getReferenceBands,
  getYMax,
  getYMin,
  IVitalChartProps,
} from "../../../shared/ReusableComponents/VitalChart";
import { AppConfigurationHelper } from "../../../services/AppConfigurationHelper";
import Grid from "@mui/material/Grid2";
import { Heading } from "../../../shared/ReusableComponents";
import {
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import moment from "moment";
import { chartBandColors } from "./VitalCharts";

export const BPChart = (props: IVitalChartProps) => {
  const [selectedRange, setSelectedRange] = useState(1);
  const [filteredChartData, setFilteredChartData] = useState(props.chartData);

  React.useEffect(() => {
    setFilteredChartData(
      selectedRange === -1
        ? props.chartData
        : props.chartData.filter((x: any) => {
            return moment(x.Date, "DD-MM-YYYY HH:mm").isAfter(
              moment().startOf("D").add(-selectedRange, "day")
            );
          })
    );
  }, [selectedRange, props.chartData]);

  const chartsConfig: any =
    AppConfigurationHelper.Instance().chartsConfig[props.configKey];

  const YMin = getYMin(chartsConfig);
  const YMax = getYMax(chartsConfig);

  // using Customized gives you access to all relevant chart props
  const CustomizedRectangle = (innerProps: any) => {
    const { formattedGraphicalItems } = innerProps;
    // get first and second series in chart
    const firstSeries = formattedGraphicalItems[0];
    const secondSeries = formattedGraphicalItems[1];

    // render custom content using points from the graph
    return firstSeries?.props?.points.map(
      (firstSeriesPoint: any, index: number) => {
        if (!firstSeriesPoint.node.y) {
          return undefined;
        }
        const secondSeriesPoint = secondSeries?.props?.points[index];
        const yDifference = firstSeriesPoint.y - secondSeriesPoint.y;

        return (
          <React.Fragment key={firstSeriesPoint.payload.name}>
            <Rectangle
              key={firstSeriesPoint.payload.name}
              width={10}
              height={10}
              x={secondSeriesPoint.x}
              y={firstSeriesPoint.y}
              fill={props.primaryLine?.color}
            />
            <Rectangle
              key={firstSeriesPoint.payload.name}
              width={10}
              height={yDifference + 10}
              x={secondSeriesPoint.x}
              y={secondSeriesPoint.y}
              fill="#737373"
            />
            <Rectangle
              key={firstSeriesPoint.payload.name}
              width={10}
              height={10}
              x={secondSeriesPoint.x}
              y={secondSeriesPoint.y}
              fill={props.secondaryLine?.color}
            />
          </React.Fragment>
        );
      }
    );
  };

  const ReferenceBands = (chartProps: any) => {
    return getReferenceBands(chartProps, chartsConfig, YMax, YMin);
  };

  const CustomAxisLabel = (axisProps: any) => {
    return getCustomAxisLabel(axisProps, props.labelColor, props.axisFontSize);
  };

  const CustomTooltip = (tooltipProps: any) => {
    return getCustomTooltip(tooltipProps);
  };

  const chartHeight = getChartHeight(chartsConfig.tickCount);
  return (
    <Grid container spacing={0} className="bp-chart">
      <Grid size={8.5}>
        <Heading
          text={props.chartTitle}
          isBold={true}
          type={"h5"}
          classes="text-ellipsis"
        />
      </Grid>
      <Grid size={3.5}>
        <FormControl fullWidth>
          <InputLabel id={`label-${props.configKey}`}>
            Day Range Filter
          </InputLabel>
          <Select
            variant="outlined"
            required={false}
            labelId={`label-${props.configKey}`}
            value={`${selectedRange}`}
            fullWidth
            label="Day Range Filter"
            onChange={(event: SelectChangeEvent) => {
              setSelectedRange(Number(event.target.value));
            }}
          >
            <MenuItem value={-1}>All records</MenuItem>
            <MenuItem value={1}>Last one day</MenuItem>
            <MenuItem value={2}>Last 2 days</MenuItem>
            <MenuItem value={4}>Last 4 days</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid size={12} style={props.cssStyle} sx={{ paddingBottom: "10px" }}>
        <Alert
          style={{
            paddingTop: "10px",
            paddingLeft: "10px",
            color: "rgb(2, 136, 209)",
            backgroundColor: "white",
            fontWeight: "bold",
          }}
          variant="outlined"
          severity="info"
        >
          Only systolic blood pressure will be used to trigger and display
          color-coded zones.
        </Alert>
        <ChartRangeContent configKey={props.configKey} />
        <ResponsiveContainer height={chartHeight}>
          <ScatterChart
            data={filteredChartData}
            margin={{
              top: 15,
              right: 15,
              left: 15,
              bottom: 25,
            }}
          >
            <XAxis
              tick={<CustomAxisLabel />}
              dataKey={props.XAxisKey}
              padding={{
                left: AppConfigurationHelper.Instance().chartsXAxisPadding,
                right: AppConfigurationHelper.Instance().chartsXAxisPadding,
              }}
              style={{ fill: props.labelColor }}
              label={{
                style: {
                  textAnchor: "middle",
                  fontSize: props.labelFontSize,
                },
                value: "Observations",
                position: "bottom",
                textAnchor: "middle",
                fill: props.labelColor,
              }}
            />
            <YAxis
              domain={[YMin, YMax]}
              style={{ fill: props.labelColor }}
              tickCount={chartsConfig.tickCount}
              interval={chartsConfig.interval}
              tick={{ fontSize: props.axisFontSize }}
              label={{
                style: { textAnchor: "middle", fontSize: props.labelFontSize },
                value: props.YAxisLabelValue,
                angle: -90,
                position: "insideLeft",
                textAnchor: "middle",
                fill: props.labelColor,
              }}
            />
            <Tooltip cursor={false} content={CustomTooltip} />
            <Legend
              verticalAlign="top"
              align="left"
              wrapperStyle={{
                width: "500px",
                padding: "15px 0 5px 70px",
              }}
            />
            <ReferenceArea shape={<ReferenceBands />} />
            {/* <CartesianGrid strokeDasharray="0 0" /> */}
            {/* <Line
              type="monotone"
              dataKey={props.primaryLine?.dataKey}
              name={props.primaryLine?.label}
              connectNulls
              stroke={props.primaryLine?.color}
              strokeWidth={2}
              dot={false}
              legendType="none"
              tooltipType="none"
            /> */}
            <Customized component={CustomizedRectangle} />
            <Scatter
              name={props.primaryLine.label}
              dataKey={props.primaryLine.dataKey}
              fill={props.primaryLine.color}
              stroke={props.primaryLine.color}
              strokeWidth={2}
              shape="square"
              line={true}
              legendType="square"
            />
            <Scatter
              dataKey={props.secondaryLine?.dataKey}
              fill={props.secondaryLine?.color}
              name={props.secondaryLine?.label}
              stroke={props.secondaryLine?.color}
              strokeWidth={2}
              shape="square"
              legendType="square"
            />
          </ScatterChart>
        </ResponsiveContainer>
      </Grid>
    </Grid>
  );
};
